import { Button } from "@2u-landing-pages/components";
import { usePageState } from "@2u-landing-pages/providers";
import EventClient from "@2u-landing-pages/utils/event-client";
import { Drawer as BricksDrawer, ButtonGroup, Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import { Banner } from "../Banner";
import { Form } from "../Form";
import { DrawerProps } from "./Drawer.types";

import { TypeFormWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
// @ts-ignore
import styles from "./Drawer.module.scss";

const componentMap = {
  form: Form,
};

interface MultiProgramDrawerProps {
  children?: React.ReactNode;
  topSlot?: React.ReactNode;
}

const MultiProgramDrawer: React.FC<MultiProgramDrawerProps> = ({ children, topSlot }) => {
  return React.isValidElement(children) ? React.cloneElement(children, { topSlot } as any) : children;
};

export const Drawer: React.FC<DrawerProps> = ({ fields, inspector = () => null, isLoading, phoneNumber, parseMetadata, children }) => {
  const { state, dispatch } = usePageState();
  const { closeButton, banner, template } = fields || {};
  const Component = children
    ? MultiProgramDrawer
    : componentMap[(template?.sys?.contentType?.sys?.id || "") as keyof typeof componentMap];

  // Blur active element when drawer opens to prevent tooltip auto-activation
  useEffect(() => {
    if (state.isDrawerOpen && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [state.isDrawerOpen]);

  const closeDrawer = useCallback(() => {
    EventClient.applicationClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: "Drawer",
        position: "Background",
      },
    });
    dispatch({ type: "CLOSE_DRAWER" });
  }, [dispatch]);

  const handleMultiProgramClose = useCallback(() => {
    dispatch({ type: "CLOSE_DRAWER" });
  }, [dispatch]);

  const handleSingleProgramClose = useCallback(() => {
    if (closeButton?.fields?.action === "CLOSE_DRAWER") {
      dispatch({ type: closeButton.fields.action });
    }
  }, [dispatch, closeButton?.fields?.action]);

  const topSlot = useMemo(() => {
    if (state.drawerType === "multi-program" || (!closeButton?.fields && children)) {
      return (
        <Container>
          <ButtonGroup
            className={cn(styles.buttonGroup, styles.multiProgramClose)}
            placement="center"
            breakpoints={{ md: { placement: "right", layout: "inline" } }}
          >
            <Button v2 size="large" variant="plain" icon="x" onClick={handleMultiProgramClose} location="Drawer">
              Close
            </Button>
          </ButtonGroup>
        </Container>
      );
    }

    if (closeButton && closeButton?.fields) {
      return (
        <ButtonGroup placement="center" className={styles.buttonWrapper}>
          <Button
            variant={closeButton?.fields?.variation}
            color={closeButton?.fields?.color}
            size={closeButton?.fields?.size}
            icon={closeButton?.fields?.icon}
            onClick={handleSingleProgramClose}
            location="Drawer"
          >
            {closeButton?.fields?.label}
          </Button>
        </ButtonGroup>
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.drawerType, closeButton?.fields, children, handleMultiProgramClose, handleSingleProgramClose]);

  return (
    <Fragment>
      {banner && (
        <Banner
          className={styles.banner}
          {...banner?.fields}
          inspector={inspector}
          phoneNumber={phoneNumber}
          parseMetadata={parseMetadata}
          location="Drawer"
        />
      )}
      {Component && (
        <BricksDrawer className={styles.drawer} isOpen={state.isDrawerOpen} onClose={closeDrawer} position="bottom">
          <Component
            {...(children ? { children } : (template?.fields as TypeFormWithoutUnresolvableLinksResponse["fields"]))}
            inDrawer={!children}
            isLoading={isLoading}
            topSlot={topSlot}
            phoneNumber={phoneNumber}
            parseMetadata={parseMetadata}
          />
        </BricksDrawer>
      )}
    </Fragment>
  );
};
